import type { PlanAutomation, PlanAutomationStatus, PlannedPostRead } from '../../types/api';

/**
 * Words and marks for a plan that runs itself (migration 0033). One table, so
 * the modal, the month chip and the upcoming list cannot disagree about what a
 * status is called.
 */

/** The three ways a plan can go out, in the order the form offers them. */
export const AUTOMATION_OPTIONS: readonly { value: PlanAutomation; label: string }[] = [
  { value: 'none', label: 'Plan only' },
  { value: 'review', label: 'Create for my review' },
  { value: 'auto', label: 'Post automatically' },
];

/** What each choice does, said under the chips. */
export const AUTOMATION_HINTS: Readonly<Record<PlanAutomation, string>> = {
  none: 'An intention on the calendar. You create the post yourself when you are ready.',
  review:
    'Created the day before, then held for you to pick the caption and approve. Nothing is posted.',
  auto: 'Created at the time you set, the caption chosen for you, and posted to LinkedIn. Posts within a few minutes of the time.',
};

/** Time of day a slot defaults to, as `HH:MM`. */
export const DEFAULT_PUBLISH_TIME = '09:00';

export function automationLabel(automation: PlanAutomation): string {
  return AUTOMATION_OPTIONS.find((option) => option.value === automation)?.label ?? automation;
}

/** The frozen states: the worker holds the plan and its slot cannot change. */
export function isClaimed(post: Pick<PlannedPostRead, 'automation_status'>): boolean {
  return post.automation_status !== 'idle';
}

/** The states a reset is legal from (the server refuses the rest with 409). */
export function isResettable(post: Pick<PlannedPostRead, 'automation_status'>): boolean {
  return post.automation_status === 'needs_review' || post.automation_status === 'failed';
}

/** A short word for a status, for a tag. `idle` and `none` say nothing. */
export function statusLabel(status: PlanAutomationStatus): string | null {
  switch (status) {
    case 'queued':
    case 'generating':
      return 'Creating';
    case 'needs_review':
      return 'Review';
    case 'publishing':
      return 'Posting';
    case 'published':
      return 'Posted';
    case 'failed':
      return 'Failed';
    default:
      return null;
  }
}

/**
 * The reason a plan stopped, in a sentence. The codes are the worker's
 * `automation_error` vocabulary; anything unlisted is shown as it came, which
 * is still a word rather than a stack trace.
 */
export function errorLabel(code: string | null): string | null {
  switch (code) {
    case null:
      return null;
    case 'quota':
      return 'No posts left this month.';
    case 'no_owner':
      return 'This brand has no active owner to post as.';
    case 'queue_unavailable':
      return 'We could not start this post. Reset to try again.';
    case 'pipeline_failed':
      return 'This post failed. Reset to try again.';
    case 'no_eligible_variant':
      return 'Neither caption passed the fact check. Pick one yourself.';
    case 'linkedin_not_connected':
      return 'No LinkedIn account is connected. Connect one, then publish from the review page.';
    case 'linkedin_reconnect_required':
      return 'The LinkedIn connection has expired. Reconnect, then publish from the review page.';
    case 'integration_disabled':
      return 'LinkedIn publishing is not configured on this deployment.';
    case 'publish_failed':
      return 'Posting to LinkedIn failed. Retry from the review page.';
    case 'plan_required':
      // Parked by the worker: the plan lost Autopilot or publishing.
      return 'Needs a plan with Autopilot';
    default:
      return code;
  }
}

/**
 * One line for a plan's automation, for a tooltip or a meta line: the mode,
 * then where it got to, then why it stopped.
 */
export function describeAutomation(post: PlannedPostRead): string | null {
  if (post.automation === 'none') return null;
  const parts = [automationLabel(post.automation)];
  const status = statusLabel(post.automation_status);
  if (status) parts.push(status);
  const reason = errorLabel(post.automation_error);
  if (reason) parts.push(reason);
  return parts.join(' · ');
}
